"use client"
import { useState, useEffect } from "react"
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  Divider,
  IconButton,
} from "@mui/material"
import {
  Dashboard as DashboardIcon,
  ShoppingCart,
  Assessment,
  Settings,
  Factory,
  ExpandLess,
  ExpandMore,
  Group,
  Business,
  SupervisorAccount,
  Store,
  Person,
  PersonAdd,
  CalendarToday,
  AccountBalance,
  ChevronLeft, // Imported from @mui/icons-material
  ChevronRight, // Imported from @mui/icons-material
  PrecisionManufacturing, // For Manufacture
  Category, // For Product
} from "@mui/icons-material"
import { Link, useLocation } from "react-router-dom"

import React from "react";
import Can from "../../components/Can";

const menuItems = [
  { id: "dashboard", title: "Dashboard", icon: DashboardIcon, path: "/amanager/dashboard" },
  {
    id: "mpoManage",
    title: "MPO Manage",
    icon: AccountBalance,

    subItems: [
      { id: "account-report", title: "Account Report", path: "/accounts/account-report" },
      { id: "profit-calculation", title: "Profit Calculation", path: "/accounts/profit-calculation" },
      { id: "expense-manage", title: "Expense Manage", path: "/accounts/expense-manage" },
      { id: "deposit-manage", title: "Deposit Manage", path: "/accounts/deposit-manage" },
      { id: "salary-manage", title: "Salary Manage", path: "/accounts/salary-manage" },
      { id: "payment-receive", title: "Payment Receive", path: "/accounts/payment-receive" },
      { id: "payment-out", title: "Payment Out", path: "/accounts/payment-out" },
      { id: "payment-rec-report", title: "Payment Rec. Report", path: "/accounts/payment-rec-report" },
      { id: "payment-out-report", title: "Payment Out Report", path: "/accounts/payment-out-report" },
    ],
  },
  {
    id: "market-colllection",
    title: "Market Colllection",
    icon: Assessment,

    subItems: [
      { id: "loan-receive", title: "Loan Receive", path: "/balance-sheet/loan-receive" },
      { id: "loan-pay", title: "Loan Pay", path: "/balance-sheet/loan-pay" },
      { id: "fixed-assets", title: "Fixed Assets", path: "/balance-sheet/fixed-assets" },
      { id: "fa-purchase", title: "FA Purchase", path: "/balance-sheet/fa-purchase" },
      { id: "balance-sheet-report", title: "Balance Sheet", path: "/balance-sheet/report" },
    ],
  },

  {
    id: "sales-reports",
    title: "SalesSales Reports",
    icon: ShoppingCart,
    subItems: [
      { id: "order-manage", title: "Order Manage", path: "/sales/order-manage" },
      { id: "sale-manage", title: "Sale Manage", path: "/sales/sale-manage" },
      { id: "all-sales-reports", title: "All Sales Reports", path: "/sales/all-sales-reports" },
    ],
  },

  {
    id: "payment-rec-report",
    title: "Pay Rec.Report",
    icon: Group,
    subItems: [
      { id: "supplier-manage", title: "Supplier Manage", icon: Business, path: "/hrm/supplier-manage" },
      {
        id: "national-sales-manager",
        title: "N S Manager", // Changed from "National Sales Manager" to "N S Manager"

        path: "/hrm/national-sales-manager"
      },


    ],
  },


  {
    id: "settings",
    title: "Settings",
    icon: Settings,
    path: "/settings",
    permission: "addroles.permission",

  },
]

const VendorSidebar = ({ open, isMobile, setSidebarOpen }) => {
  const location = useLocation()
  const [expandedMenus, setExpandedMenus] = useState([])

  // Effect to open the relevant submenu on page load/path change
  useEffect(() => {
    const currentPath = location.pathname
    let activeParentId = null

    for (const item of menuItems) {
      if (item.subItems) {
        if (item.subItems.some((subItem) => currentPath.startsWith(subItem.path))) {
          activeParentId = item.id
          break
        }
      } else if (item.path === currentPath) {
        // If a top-level item is active, ensure no submenu is expanded
        activeParentId = null
        break
      }
    }

    if (activeParentId) {
      setExpandedMenus([activeParentId]) // Only one parent ID in the array
    } else {
      setExpandedMenus([]) // Collapse all if no sub-menu is active or a top-level item is active
    }
  }, [location.pathname, menuItems]) // menuItems added to dependency array

  // Function to toggle menu expansion, ensuring only one is open at a time
  const toggleMenu = (menuId) => {
    setExpandedMenus((prev) => {
      if (prev.includes(menuId)) {
        // If the clicked menu is already open, close it
        return prev.filter((id) => id !== menuId)
      } else {
        // If the clicked menu is closed, open it and close all others
        return [menuId]
      }
    })
  }

  const handleMenuItemClick = () => {
    if (isMobile) {
      setSidebarOpen(false)
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header section of the sidebar */}
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Factory color="primary" />
        {open && (
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              ERP System
            </Typography>
            <Typography variant="caption" color="text.secondary">
              AM Management Panel
            </Typography>
          </Box>
        )}
        {isMobile && open && (
          <IconButton onClick={() => setSidebarOpen(false)} size="small" sx={{ ml: "auto" }}>
            <ChevronLeft />
          </IconButton>
        )}
      </Box>
      <Divider />
      {/* Menu Items List */}
      <List sx={{ pt: 1, flexGrow: 1, overflowY: "auto" }} className="custom-sidebar-scrollbar">
        {menuItems.map((item) => {
          const Icon = item.icon
          const hasSubItems = item.subItems && item.subItems.length > 0
          const isExpanded = expandedMenus.includes(item.id)
          const isActiveTopLevel = location.pathname === item.path // For top-level items

          return (

            <Box key={item.id}>
              {hasSubItems ? (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleMenu(item.id)}
                    sx={{
                      minHeight: 48,
                      // Parent menu button itself should not have a background when a sub-item is active
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)", // Lighter grey on hover
                      },
                      // No specific selected style for the parent button's background,
                      // as the active sub-item will provide the visual cue.
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: open ? 56 : "auto", justifyContent: "center" }}>
                      {Icon && <Icon />}
                    </ListItemIcon>
                    {open && (
                      <>
                        <ListItemText primary={item.title} />
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </>
                    )}
                  </ListItemButton>
                </ListItem>
              ) : (
                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    sx={{
                      minHeight: 48,
                      backgroundColor: isActiveTopLevel ? "rgba(0, 0, 0, 0.08)" : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "rgba(0, 0, 0, 0.08)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: open ? 56 : "auto", justifyContent: "center" }}>
                      <Icon />
                    </ListItemIcon>
                    {open && <ListItemText primary={item.title} />}
                  </ListItemButton>
                </ListItem>
              )}
              {hasSubItems && open && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => {
                      const SubIcon = subItem.icon
                      return (

                        <ListItem disablePadding key={subItem.id}>
                          <ListItemButton
                            component={Link}
                            to={subItem.path}
                            sx={{
                              pl: 4,
                              backgroundColor:
                                location.pathname === subItem.path ? "rgba(0, 0, 0, 0.08)" : "transparent",
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                              },
                              "&.Mui-selected": {
                                backgroundColor: "rgba(0, 0, 0, 0.08)",
                                "&:hover": {
                                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                              },
                            }}
                            onClick={handleMenuItemClick} // Close sidebar on mobile when sub-item is clicked
                          >
                            {SubIcon && (
                              <ListItemIcon sx={{ minWidth: 40, justifyContent: "center" }}>
                                <SubIcon sx={{ fontSize: 20 }} />
                              </ListItemIcon>
                            )}
                            <ListItemText primary={subItem.title} />
                          </ListItemButton>
                        </ListItem>

                      )
                    })}
                  </List>
                </Collapse>
              )}
            </Box>

          )
        })}
      </List>
      <Divider sx={{ mt: "auto" }} />
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: open ? "flex-end" : "center",
          alignItems: "center",
        }}
      >
        <ListItemButton onClick={() => setSidebarOpen(!open)} sx={{ width: "auto", minHeight: 48 }}>
          {open ? <ChevronLeft /> : <ChevronRight />}
        </ListItemButton>
      </Box>
    </Box >
  )
}
export default VendorSidebar
